import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EmailEvent } from '../../models/email-event';
import { MaterialMaster } from '../../models/material-master';
import { PlantMaster } from '../../models/plant-master';
import { Subscription } from '../../models/subscription';
import { SupplierProfile } from '../../models/supplier-profile';
import { UserDetail } from '../../models/user-detail';
import { VendorMaster } from '../../models/vendor-master';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  // User ---------------------------------------------------------------------
  private readonly _user = new BehaviorSubject<UserDetail>(new UserDetail());
  readonly user$ = this._user.asObservable();

  get user(): UserDetail {
    return this._user.getValue();
  }

  private set user(val: UserDetail) {
    this._user.next(val);
  }

  assignUser(user: UserDetail) {
    this.user = user;
  }

  // Supplier Profile ---------------------------------------------------------
  private readonly _supplierProfiles = new BehaviorSubject<SupplierProfile[]>(
    []
  );
  readonly supplierProfiles$ = this._supplierProfiles.asObservable();

  get supplierProfiles(): SupplierProfile[] {
    return this._supplierProfiles.getValue();
  }

  private set supplierProfiles(val: SupplierProfile[]) {
    this._supplierProfiles.next(val);
  }

  initSupplierProfiles(data: SupplierProfile[]) {
    this.supplierProfiles = data;
  }

  updateSupplierProfile(newProfile: SupplierProfile) {
    let oldProfile = this.supplierProfiles.find((profile) => {
      return (
        profile.supplierIdentity === newProfile.supplierIdentity &&
        profile.vendCode === newProfile.vendCode &&
        profile.plantCode === newProfile.plantCode
      );
    });

    if (oldProfile) {
      const index = this.supplierProfiles.indexOf(oldProfile);
      this.supplierProfiles[index] = newProfile;
      this.supplierProfiles = [...this.supplierProfiles];
    }
  }

  addSupplierProfile(newProfile: SupplierProfile) {
    // Update Supplier profile state
    this.supplierProfiles = [...this.supplierProfiles, newProfile];

    // Update Supplier list state
    if (
      !this.supplierList.find(
        (list) => list.supplierIdentity === newProfile.supplierIdentity
      )
    ) {
      this.supplierList = [...this.supplierList, newProfile];
    }
  }

  deleteSupplierProfile(deleteProfile: SupplierProfile) {
    var profile = this.supplierProfiles.filter(
      (pf) =>
        !(
          pf.supplierIdentity === deleteProfile.supplierIdentity &&
          pf.plantCode === deleteProfile.plantCode &&
          pf.vendCode === deleteProfile.vendCode
        )
    );

    this.initSupplierProfiles(profile);
  }

  // Supplier List ------------------------------------------------------------
  private readonly _supplierList = new BehaviorSubject<SupplierProfile[]>([]);
  readonly supplierList$ = this._supplierList.asObservable();

  get supplierList(): SupplierProfile[] {
    return this._supplierList.getValue();
  }

  private set supplierList(val: SupplierProfile[]) {
    this._supplierList.next(val);
  }

  initSupplierList(data: SupplierProfile[]) {
    this.supplierList = data;
  }

  deleteSupplierList(deleteProfile: SupplierProfile) {
    var list = this.supplierList.filter(
      (pf) => !(pf.supplierIdentity === deleteProfile.supplierIdentity)
    );

    this.initSupplierList(list);
  }

  // User Subscriptions -------------------------------------------------------
  private readonly _userSubscriptions = new BehaviorSubject<Subscription[]>([]);
  readonly userSubscriptions$ = this._userSubscriptions.asObservable();

  get userSubscriptions(): Subscription[] {
    return this._userSubscriptions.getValue();
  }

  private set userSubscriptions(val: Subscription[]) {
    this._userSubscriptions.next(val);
  }

  initUserSubscriptions(data: Subscription[]) {
    this.userSubscriptions = data;
  }

  addUserSubscriptions(newSubscription: Subscription) {
    // Update userSubscriptions state
    this.userSubscriptions = [...this.userSubscriptions, newSubscription];
  }

  updateUserSubscriptions(newSubscription: Subscription) {
    // Update userSubscriptions state
    let oldUserSub = this.userSubscriptions.find((userSub) => {
      return (
        userSub.eventName === newSubscription.eventName &&
        userSub.rowKey === newSubscription.rowKey
      );
    });

    if (oldUserSub) {
      const index = this.userSubscriptions.indexOf(oldUserSub);
      this.userSubscriptions[index] = newSubscription;
      this.userSubscriptions = [...this.userSubscriptions];
    }
  }

  // Events -------------------------------------------------------------------
  private readonly _events = new BehaviorSubject<EmailEvent[]>([]);
  readonly events$ = this._events.asObservable();

  get events(): EmailEvent[] {
    return this._events.getValue();
  }

  private set events(val: EmailEvent[]) {
    this._events.next(val);
  }

  initEvents(data: EmailEvent[]) {
    this.events = data;
  }

  // Subscriptions ------------------------------------------------------------
  private readonly _subscriptions = new BehaviorSubject<Subscription[]>([]);
  readonly subscriptions$ = this._events.asObservable();

  get subscriptions(): Subscription[] {
    return this._subscriptions.getValue();
  }

  private set subscriptions(val: Subscription[]) {
    this._subscriptions.next(val);
  }

  initSubscriptions(data: Subscription[]) {
    this.subscriptions = data;
  }

  clearSubscriptions() {
    this.subscriptions.splice(0, this.subscriptions.length);
  }

  addSubscriptions(newSubscriptions: Subscription[]) {
    // Update Supplier profile state
    this.subscriptions = [...this.subscriptions, ...newSubscriptions];
  }

  addSubscription(newSubscriptions: Subscription) {
    // Update Supplier profile state
    this.subscriptions.push(newSubscriptions);
  }

  updateSubscriptions(newSubscription: Subscription) {
    // Update userSubscriptions state
    let oldUserSub = this.subscriptions.find((userSub) => {
      return (
        userSub.eventName === newSubscription.eventName &&
        userSub.rowKey === newSubscription.rowKey &&
        userSub.partitionKey === newSubscription.partitionKey
      );
    });

    if (oldUserSub) {
      const index = this.subscriptions.indexOf(oldUserSub);
      this.subscriptions[index] = newSubscription;
      this.subscriptions = [...this.subscriptions];
    }
  }

  // Plant ------------------------------------------------------------
  private readonly _plants = new BehaviorSubject<PlantMaster[]>([]);
  readonly plants$ = this._events.asObservable();

  get plants(): PlantMaster[] {
    return this._plants.getValue();
  }

  private set plants(val: PlantMaster[]) {
    this._plants.next(val);
  }

  initPlants(data: PlantMaster[]) {
    this.plants = data;
  }

  mergePlants(data: PlantMaster[]) {
    this.plants = [...this.plants, ...data];
  }

  // Vendor ------------------------------------------------------------
  private readonly _vendors = new BehaviorSubject<VendorMaster[]>([]);
  readonly Vendors$ = this._events.asObservable();

  get vendors(): VendorMaster[] {
    return this._vendors.getValue();
  }

  private set vendors(val: VendorMaster[]) {
    this._vendors.next(val);
  }

  initVendors(data: VendorMaster[]) {
    this.vendors = data;
  }

  mergeVendors(data: VendorMaster[]) {
    this.vendors = [...this.vendors, ...data];
  }

  // Material ------------------------------------------------------------
  private readonly _materials = new BehaviorSubject<MaterialMaster[]>([]);
  readonly Materials$ = this._events.asObservable();

  get materials(): MaterialMaster[] {
    return this._materials.getValue();
  }

  private set materials(val: MaterialMaster[]) {
    this._materials.next(val);
  }

  initMaterials(data: MaterialMaster[]) {
    this.materials = data;
  }

  mergeMaterials(data: MaterialMaster[]) {
    this.materials = [...this.materials, ...data];
  }

  addMaterials(data: MaterialMaster) {
    this.materials.push(data);
  }
}
